import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import { DataExportPanel } from '../components/admin/DataExportPanel';

export default function CompanySettingsPage({ currentUser, onBackToGears }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '', website: '', logo_url: '',
    smtp_email: '', smtp_password: '',
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/company/settings`);
      setCompany(res.data);
      setForm({
        name: res.data.name || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        logo_url: res.data.logo_url || '',
        smtp_email: res.data.smtp_email || '',
        smtp_password: res.data.smtp_password || '',
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/company/settings`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowed.includes(file.type)) {
      alert('Please upload a PNG, JPEG, WebP, SVG, or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum 5MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/company/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update('logo_url', res.data.logo_url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="company-settings-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToGears}
              className="text-gray-400 hover:text-gray-700 transition-colors"
              data-testid="settings-back-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Company Settings</h1>
              <p className="text-xs text-gray-500">{company?.name}</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              saved 
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-900 hover:bg-gray-800 text-white disabled:opacity-50'
            }`}
            data-testid="save-settings-btn"
          >
            {saved ? 'Saved' : saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Company Info */}
        <Section title="Company Information" description="This appears on your dashboard, PDFs, and emails.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Company Name" value={form.name} onChange={v => update('name', v)} testId="settings-company-name" />
            <Field label="Contact Email" type="email" value={form.email} onChange={v => update('email', v)} testId="settings-email" />
            <Field label="Phone Number" value={form.phone} onChange={v => update('phone', v)} testId="settings-phone" placeholder="1-888-555-0000" />
            <Field label="Website" value={form.website} onChange={v => update('website', v)} testId="settings-website" placeholder="https://www.yourcompany.com" />
          </div>
          <div className="mt-5">
            <Field label="Address" value={form.address} onChange={v => update('address', v)} testId="settings-address" placeholder="123 Main St, City, Province" />
          </div>
        </Section>

        {/* Branding */}
        <Section title="Branding" description="Upload your logo to customize the platform. Logo appears on the gears page and PDFs.">
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-2">Upload Logo</label>
              <div className="flex items-center gap-3">
                <label
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                    uploading ? 'bg-gray-100 text-gray-400' : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  data-testid="settings-upload-logo-btn"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Choose File
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    className="hidden"
                    data-testid="settings-logo-file-input"
                  />
                </label>
                <span className="text-xs text-gray-400">PNG, JPEG, WebP, SVG, GIF — max 5MB</span>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-x-0 top-1/2 border-t border-gray-200" />
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">or enter a URL</span></div>
            </div>
            <Field 
              label="Logo URL" 
              value={form.logo_url} 
              onChange={v => update('logo_url', v)} 
              testId="settings-logo-url"
              placeholder="https://example.com/logo.png"
            />
          </div>
          {form.logo_url && (
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Preview:</p>
              <img 
                src={form.logo_url} 
                alt="Logo preview" 
                className="h-16 object-contain"
                onError={e => { e.target.style.display = 'none'; }}
                data-testid="logo-preview"
              />
            </div>
          )}
        </Section>

        {/* Email Configuration */}
        <Section title="Email Configuration" description="Optional. Configure your SMTP settings so emails are sent from your own address. If not configured, emails will be sent from the platform address.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field 
              label="SMTP Email" 
              type="email" 
              value={form.smtp_email} 
              onChange={v => update('smtp_email', v)} 
              testId="settings-smtp-email"
              placeholder="you@yourcompany.com"
            />
            <Field 
              label="App Password" 
              type="password" 
              value={form.smtp_password} 
              onChange={v => update('smtp_password', v)} 
              testId="settings-smtp-password"
              placeholder="Gmail App Password"
            />
          </div>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-blue-800 text-sm font-medium mb-1">How to set up Gmail SMTP</p>
            <ol className="text-blue-700 text-xs space-y-1 list-decimal list-inside">
              <li>Go to your Google Account &rarr; Security &rarr; 2-Step Verification (must be enabled)</li>
              <li>Search for "App passwords" in Google Account settings</li>
              <li>Create a new app password for "Mail"</li>
              <li>Enter your Gmail address and the generated app password above</li>
            </ol>
          </div>
        </Section>

        {/* Enabled Modules (read-only for company admins) */}
        <Section title="Enabled Modules" description="These are the modules available to your company. Contact the platform administrator to change.">
          <div className="flex flex-wrap gap-2">
            {(company?.modules_enabled || []).map(mod => (
              <span 
                key={mod} 
                className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium capitalize"
                data-testid={`enabled-module-${mod}`}
              >
                {mod.replace(/-/g, ' ')}
              </span>
            ))}
          </div>
        </Section>

        {/* Platform Setup Section - for admins only */}
        {currentUser?.rawRole === 'admin' && (
          <Section title="Data Exports" description="Download tenant-scoped JSON exports or a full ZIP bundle for migration/backups.">
            <DataExportPanel isPlatformAdmin={!!currentUser?.isPlatformAdmin} defaultCompanyId={currentUser?.companyId || ''} />
          </Section>
        )}

        {/* Platform Setup Section - for admins only */}
        {currentUser?.rawRole === 'admin' && (
          <Section title="Platform Administration" description="One-time setup to initialize the Reify Systems platform admin account and backfill tool permissions.">
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={async () => {
                  if (!window.confirm('This will:\n1. Create the Platform Admin account (contact@reifysystems.com)\n2. Create the Dev Sandbox company\n3. Create Groundhogs company record (if missing)\n4. Tag all data with company ID\n5. Backfill tool permissions\n\nSafe to run multiple times. Continue?')) return;
                  try {
                    const res = await axios.post(`${API}/platform/setup`);
                    const d = res.data;
                    alert(`Platform Setup Complete!\n\nPlatform Admin: ${d.platform_admin}\nDev Company: ${d.dev_company || 'N/A'}\nGroundhogs: ${d.groundhogs_migration || 'N/A'}\nLogo: ${d.groundhogs_logo || 'N/A'}\nBackfill: ${d.backfill}`);
                  } catch (err) {
                    alert('Setup failed: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="platform-setup-btn"
              >
                Run Platform Setup
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('This will find and tag any documents that are missing a company ID.\nThis fixes data that was created before multi-tenancy was enabled.\n\nSafe to run multiple times. Continue?')) return;
                  try {
                    const res = await axios.post(`${API}/platform/retag-data`);
                    const d = res.data;
                    const collections = Object.entries(d.collections_updated || {}).map(([k,v]) => `  ${k}: ${v} docs`).join('\n');
                    alert(`Re-tag Complete!\n\n${d.message}\n\n${collections || 'No untagged documents found.'}`);
                  } catch (err) {
                    alert('Re-tag failed: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                className="px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="retag-data-btn"
              >
                Re-tag Missing Data
              </button>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-0.5">{title}</h2>
      <p className="text-xs text-gray-500 mb-5">{description}</p>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 font-medium mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
        data-testid={testId}
      />
    </div>
  );
}
